import { Button } from "@/components/ui/button";
import { Database } from "lucide-react";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { useState, useEffect } from "react";
import { DayTitleManager } from "./day-title/DayTitleManager";
import { ExerciseItem } from "./exercise/ExerciseItem";
import { ExerciseSelectionDialog } from "./exercise/ExerciseSelectionDialog";
import { Exercise } from "./exercise/types/ExerciseTypes";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslations } from "@/hooks/useTranslations";

interface WorkoutDayCardProps {
  dayIndex: number;
  dayTitle: string;
  exercises: Exercise[];
  onAddExercise: (dayIndex: number) => void;
  onRemoveExercise: (dayIndex: number, exerciseIndex: number) => void;
  onUpdateExercise: (dayIndex: number, exerciseIndex: number, field: keyof Exercise, value: string | number | string[] | boolean) => void;
  onMoveExercise: (dayIndex: number, exerciseIndex: number, direction: 'up' | 'down') => void;
  onUpdateDayTitle: (dayIndex: number, title: string) => void;
  onRemoveDay: (dayIndex: number) => void;
  onMoveDay: (dayIndex: number, direction: 'up' | 'down') => void;
  isExpanded: boolean;
  totalDays: number;
}

export function WorkoutDayCard({
  dayIndex,
  dayTitle,
  exercises,
  onAddExercise,
  onRemoveExercise,
  onUpdateExercise,
  onMoveExercise,
  onUpdateDayTitle,
  onRemoveDay,
  onMoveDay,
  isExpanded,
  totalDays
}: WorkoutDayCardProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [showExerciseLibrary, setShowExerciseLibrary] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslations("workouts");

  useEffect(() => {
    setIsOpen(isExpanded);
  }, [isExpanded]);

  const handleSelectExercise = (exercise: Exercise) => {
    if (!exercise) return;
    
    onAddExercise(dayIndex);
    const newExerciseIndex = exercises.length;
    
    // Atualizar nome com o nome do exercício selecionado
    onUpdateExercise(dayIndex, newExerciseIndex, 'name', exercise.name || "");
    
    // Se o exercício tiver URL de imagem, defina-o como image_url
    if (exercise.image_url) {
      onUpdateExercise(dayIndex, newExerciseIndex, 'image_url', exercise.image_url);
      console.log("Imagem definida para o exercício:", exercise.image_url);
    }
    
    // Adicionar grupo muscular e instruções
    if (exercise.body_part) {
      onUpdateExercise(dayIndex, newExerciseIndex, 'body_part', exercise.body_part);
    }
    
    if (exercise.target_muscle) {
      onUpdateExercise(dayIndex, newExerciseIndex, 'target_muscle', exercise.target_muscle);
    }
    
    // Marcar exercício como personalizado se necessário
    if (exercise.isCustom) {
      onUpdateExercise(dayIndex, newExerciseIndex, 'isCustom', true);
    }
    
    // Adicionar instruções - Usando as instruções do exercício da biblioteca, se disponíveis
    onUpdateExercise(
      dayIndex, 
      newExerciseIndex, 
      'instructions', 
      Array.isArray(exercise.instructions) && exercise.instructions.length > 0 
        ? exercise.instructions 
        : ["Posicione-se corretamente", "Mantenha a forma durante todo o movimento", "Respire adequadamente"]
    );
    
    // Definir valores padrão para séries e repetições
    onUpdateExercise(dayIndex, newExerciseIndex, 'sets', 3);
    onUpdateExercise(dayIndex, newExerciseIndex, 'reps', "10-12");
  };

  const handleSelectMultipleExercises = (
    selectedExercises: Exercise[], 
    defaultSets: number, 
    defaultReps: string,
    defaultTime?: string
  ) => {
    if (!selectedExercises || !Array.isArray(selectedExercises) || selectedExercises.length === 0) return;
    
    // Limpar os exercícios existentes para este dia
    exercises.forEach((_, index) => {
      onRemoveExercise(dayIndex, 0);
    });
    
    // Adicionar cada exercício selecionado
    selectedExercises.forEach((exercise, index) => {
      if (!exercise) return;
      
      onAddExercise(dayIndex);
      
      // Atualizar nome e outros detalhes
      onUpdateExercise(dayIndex, index, 'name', exercise.name || "");
      
      // Definir imagem se disponível
      if (exercise.image_url) {
        onUpdateExercise(dayIndex, index, 'image_url', exercise.image_url);
      }
      
      if (exercise.body_part) {
        onUpdateExercise(dayIndex, index, 'body_part', exercise.body_part);
      }
      
      if (exercise.target_muscle) {
        onUpdateExercise(dayIndex, index, 'target_muscle', exercise.target_muscle);
      }
      
      // Marcar exercício como personalizado se necessário
      if (exercise.isCustom) {
        onUpdateExercise(dayIndex, index, 'isCustom', true);
      }
      
      // Adicionar séries e repetições padrão
      onUpdateExercise(dayIndex, index, 'sets', defaultSets);
      onUpdateExercise(dayIndex, index, 'reps', defaultReps);
      
      // Adicionar tempo se especificado (para exercícios de cardio)
      if (defaultTime) {
        onUpdateExercise(dayIndex, index, 'time', defaultTime);
      }
      
      // Adicionar instruções - Usando as instruções do exercício da biblioteca, se disponíveis
      onUpdateExercise(
        dayIndex, 
        index, 
        'instructions', 
        Array.isArray(exercise.instructions) && exercise.instructions.length > 0 
          ? exercise.instructions 
          : ["Posicione-se corretamente", "Mantenha a forma durante todo o movimento", "Respire adequadamente"]
      );
    });
  };

  // Determine button text based on whether exercises exist
  const buttonText = exercises.length > 0 
    ? t("actions.editExercises") 
    : t("actions.addExercises");

  return (
    <div className="border-0 bg-transparent dark:bg-transparent">
      {showExerciseLibrary && (
        <ExerciseSelectionDialog 
          open={showExerciseLibrary}
          onOpenChange={setShowExerciseLibrary}
          onSelectExercise={handleSelectExercise}
          onSelectMultipleExercises={handleSelectMultipleExercises}
          currentDayExercises={exercises}
        />
      )}

      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className={`pb-2 ${isMobile ? 'px-0' : 'px-[6px]'}`}>
          <DayTitleManager
            dayIndex={dayIndex}
            dayTitle={dayTitle}
            onUpdateDayTitle={onUpdateDayTitle}
            onRemoveDay={onRemoveDay}
            onMoveDay={onMoveDay}
            isOpen={isOpen}
            totalDays={totalDays}
          />
        </div>
        
        <CollapsibleContent>
          <div className="space-y-4 pt-2">
            {exercises.map((exercise, exerciseIndex) => (
              <ExerciseItem
                key={exerciseIndex}
                exercise={exercise}
                exerciseIndex={exerciseIndex}
                dayIndex={dayIndex}
                totalExercises={exercises.length}
                onUpdateExercise={onUpdateExercise}
                onRemoveExercise={onRemoveExercise}
                onMoveExercise={onMoveExercise}
              />
            ))}

            <div className="flex">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowExerciseLibrary(true)}
                className="flex-1 h-8 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700"
              >
                <Database className="mr-2 h-3 w-3" />
                {buttonText}
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
